import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Paper,
  Alert
} from '@mui/material';
import { ExpandMore, Help } from '@mui/icons-material';
import { faqPageService } from '../services/firebase';
import { FAQPageData, FAQItem } from '../types';
import { useTranslation } from 'react-i18next';
import { getTranslatedData } from '../utils/getTranslatedData';

const FAQPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [faqData, setFaqData] = useState<FAQPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFAQData = async () => {
      try {
        setLoading(true);
        let data = await faqPageService.getFAQPageData();
        
        if (!data) {
          // Initialize with default data if no data exists
          await faqPageService.initializeFAQPageData();
          data = await faqPageService.getFAQPageData();
        }
        
        setFaqData(data);
      } catch (err: any) {
        setError(t('faq_page.loadError', { message: err.message }));
      } finally {
        setLoading(false);
      }
    };

    fetchFAQData();
  }, [t]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const faqs = faqData?.faq || [];

  return (
    <>
      <Helmet>
        <title>{t('faq_page.title')} - HatTravel</title>
        <meta name="description" content={getTranslatedData(faqData?.heroSection.description, i18n.language) || t('faq_page.description')} />
      </Helmet>
      
      <Box sx={{ 
        backgroundColor: 'var(--light-gray-white)',
        minHeight: '100vh',
        py: 8
      }}>
        {/* Hero Section */}
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                backgroundColor: 'var(--light-blue-cta)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                boxShadow: '0 8px 24px rgba(0,91,189,0.2)'
              }}>
                <Help sx={{ fontSize: 50 }} />
              </Box>
            </Box>
            
            <Typography 
              variant="h1" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                fontSize: { xs: '3rem', md: '4rem' },
                color: 'var(--navy-blue)',
                mb: 3
              }}
            >
              {getTranslatedData(faqData?.heroSection.title, i18n.language) || t('faq_page.title')}
            </Typography>
            
            <Typography 
              variant="h5" 
              color="text.secondary"
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 300,
                lineHeight: 1.6
              }}
            >
              {getTranslatedData(faqData?.heroSection.description, i18n.language) || t('faq_page.description')}
            </Typography>
          </Box>

          {/* FAQ List */}
          <Paper 
            elevation={0}
            sx={{ 
              p: { xs: 2, sm: 4 },
              backgroundColor: 'white',
              borderRadius: 4,
              boxShadow: '0 8px 24px rgba(0,0,0,0.05)',
              maxWidth: 900,
              mx: 'auto'
            }}
          >
            {[...faqs]
              .filter((faq: FAQItem) => faq.isActive)
              .sort((a: FAQItem, b: FAQItem) => a.order - b.order)
              .map((faq: FAQItem) => (
                <Accordion 
                  key={faq.id} 
                  sx={{
                    backgroundColor: 'white',
                    boxShadow: 'none',
                    borderBottom: '1px solid var(--gray-shadow)',
                    '&:last-of-type': {
                      borderBottom: 'none',
                    },
                    '&:before': {
                      display: 'none',
                    },
                    '&.Mui-expanded': {
                      margin: 0,
                    }
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore sx={{ color: 'var(--light-blue-cta)' }} />}
                    aria-controls={`${faq.id}-content`}
                    id={`${faq.id}-header`}
                    sx={{
                      py: 2,
                      '& .MuiAccordionSummary-content': {
                        alignItems: 'center',
                        gap: 2,
                      }
                    }}
                  >
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      backgroundColor: 'var(--light-gray)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--light-blue-cta)',
                      flexShrink: 0,
                      fontWeight: 700
                    }}>
                      ?
                    </Box>
                    <Typography sx={{ fontWeight: 600, color: 'var(--navy-blue)', fontSize: '1.1rem' }}>
                      {getTranslatedData(faq.question, i18n.language)}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{
                    p: 3,
                    pt: 1,
                    pl: 7,
                  }}>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                      {getTranslatedData(faq.answer, i18n.language)}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
            ))}
            {faqs.filter(faq => faq.isActive).length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Help sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }}/>
                <Typography variant="h6" color="text.secondary">
                  {t('faq_page.noFaqs')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default FAQPage; 